/**
 * Puts keyboard focus back near where it was when the element holding it leaves the page.
 *
 * Pressing Remove on a member, Allow on a request or Save in an inline editor can take the very
 * control that had focus out of the page — the row is gone, or the editor closed — and a browser
 * then drops focus on `<body>`. A keyboard or screen-reader user starts again from the top of the
 * document, having lost their place (GRP-103). A dialog gives focus back to what opened it, but
 * the opener itself often goes a moment later, when the list is read again.
 *
 * One watcher for the whole app rather than a fix at each of those places: it remembers, on every
 * focus, the ancestors of what has focus; when that element is no longer on the page and nothing
 * else took focus on purpose, focus goes to the nearest section, card, dialog or page still there —
 * to its heading, which a screen reader announces. Never to a button: a stray Enter on "Remove"
 * would act on the next row.
 */

/** Containers whose heading says where the reader is. */
const CONTAINERS = 'dialog, [role="dialog"], article, section, [role="region"], main'
const HEADINGS = 'h1, h2, h3, h4'

/** The little of an element the choice reads, so it can be decided without a browser. */
export type RescueNode = {
  readonly isConnected: boolean
  matches: (selector: string) => boolean
  querySelector: (selector: string) => unknown
}

/**
 * Where focus goes once the element that had it is gone: the heading of the nearest ancestor that
 * is still on the page and is a container a person would recognise, or that container itself.
 * `ancestors` runs outward from the lost element, as recorded while it was still attached.
 */
export const rescueTarget = <T extends RescueNode>(ancestors: readonly T[]): T | null => {
  for (const node of ancestors) {
    if (!node.isConnected || !node.matches(CONTAINERS)) continue
    return (node.querySelector(HEADINGS) as T | null) ?? node
  }
  return null
}

/** Installs the watcher on a document; returns what removes it. */
export const installFocusRescue = (root: Document): (() => void) => {
  let focused: Element | null = null
  let ancestors: Element[] = []

  const onFocusIn = (event: FocusEvent) => {
    if (!(event.target instanceof Element)) return
    focused = event.target
    ancestors = []
    for (let node = focused.parentElement; node; node = node.parentElement) ancestors.push(node)
  }

  const observer = new MutationObserver(() => {
    if (focused === null || focused.isConnected) return
    // Something else already took focus deliberately — a dialog handing it back, a new field.
    if (root.activeElement !== null && root.activeElement !== root.body) {
      focused = null
      return
    }
    const target = rescueTarget(ancestors)
    focused = null
    if (!(target instanceof HTMLElement)) return
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1')
    target.focus()
  })

  root.addEventListener('focusin', onFocusIn)
  observer.observe(root.body, { childList: true, subtree: true })
  return () => {
    root.removeEventListener('focusin', onFocusIn)
    observer.disconnect()
  }
}
